const axios = require('axios');

// Module-level cache with TTL (5s) for dashboard-wide data.
// When many widgets mount at once, only one real fetch happens.
const CACHE_TTL_MS = 5000;
const cache = {
  dashboardStats: { data: null, ts: 0, promise: null, filtersKey: '' },
  selfServiceStats: { data: null, ts: 0, promise: null, filtersKey: '' },
};

function filtersKey(f) {
  return `${f?.from || ''}|${f?.to || ''}|${(f?.subCompanyIds || []).join(',')}`;
}

export const widgetMixin = {
  props: {
    filters: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
  },
  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    formatCurrency(cents) {
      const v = (cents || 0) / 100;
      return v.toFixed(2) + ' zł';
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleString(this.$i18n.locale === 'pl' ? 'pl-PL' : 'en-US');
    },
    formatCooperationType(type) {
      return ({
        B2B_VAT: 'B2B VAT',
        B2B_NO_VAT: 'B2B bez VAT',
        FIFTY_FIFTY: 'Zlecenie 50/50',
        RENT: 'Najem',
        CONNECTION: 'Podpięcie',
        STUDENT: 'Student',
      })[type] || type;
    },
    formatZusType(type) {
      return ({
        B2B: 'B2B',
        NOT_HIRED_L4: 'Niezatrudniony z L4',
        NOT_HIRED_NO_L4: 'Niezatrudniony bez L4',
        HIRED_BELOW_WITH_L4: 'Zatrudniony poniżej z L4',
        HIRED_BELOW_NO_L4: 'Zatrudniony poniżej bez L4',
        HIRED_ABOVE: 'Zatrudniony powyżej',
        STUDENT: 'Student',
        UNDER_26_NO_L4: 'Poniżej 26 bez L4',
        UNDER_26_L4: 'Poniżej 26 z L4',
      })[type] || type || '—';
    },
    formatPayoutSchedule(type) {
      return ({
        DAILY: 'Dziennie',
        WEEKLY: 'Tygodniowo',
        EVERY_2_WEEKS: 'Co 2 tygodnie',
        MONTHLY: 'Miesięcznie',
      })[type] || type || '—';
    },
    async getJson(path, params) {
      const res = await axios({ method: 'get', url: this.apiUrl(path), params, headers: this.authHeaders() });
      return (res.data && res.data.data) || null;
    },
    async getDashboardStats() {
      const key = filtersKey(this.filters);
      const now = Date.now();
      const c = cache.dashboardStats;
      if (c.data && c.filtersKey === key && (now - c.ts) < CACHE_TTL_MS) return c.data;
      if (c.promise && c.filtersKey === key) return c.promise;
      c.filtersKey = key;
      c.promise = this.getJson('settlements/dashboard-stats', {
        startDate: this.filters?.from,
        endDate: this.filters?.to,
        ...(this.filters?.subCompanyIds ? { subCompanyIds: this.filters.subCompanyIds } : {}),
      }).then((d) => {
        c.data = d;
        c.ts = Date.now();
        c.promise = null;
        return d;
      }).catch((e) => {
        c.promise = null;
        throw e;
      });
      return c.promise;
    },
    async getSelfServiceStats() {
      const key = filtersKey(this.filters);
      const now = Date.now();
      const c = cache.selfServiceStats;
      if (c.data && c.filtersKey === key && (now - c.ts) < CACHE_TTL_MS) return c.data;
      if (c.promise && c.filtersKey === key) return c.promise;
      c.filtersKey = key;
      c.promise = this.getJson('self-service/admin/stats', {
        from: this.filters?.from,
        to: this.filters?.to,
        ...(this.filters?.subCompanyIds && this.filters.subCompanyIds.length > 0
          ? { subCompanyIds: this.filters.subCompanyIds }
          : {}),
      }).then((d) => {
        c.data = d;
        c.ts = Date.now();
        c.promise = null;
        return d;
      }).catch((e) => {
        c.promise = null;
        throw e;
      });
      return c.promise;
    },
  },
};

// Expose cache invalidation for filter changes
export function invalidateDashboardCache() {
  cache.dashboardStats = { data: null, ts: 0, promise: null, filtersKey: '' };
  cache.selfServiceStats = { data: null, ts: 0, promise: null };
}
